import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ChartBindingObject, ChartManagerService, ChartRegistryService } from "@tylertech/charting-module";
import { LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PacketProgress } from "../../../interfaces";

@Component({
    selector: "utilities-packet-progress-chart",
    templateUrl: "./packetsProgressCharts.tmpl.html",
    styleUrls: [
        "./packetsProgressCharts.component.scss"
    ],
    standalone: false
})
export class PacketsProgressChartsComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly customDataBinding: ChartBindingObject;

    @Input("chartId") private readonly _chartId: string;
    private _chartCriteria: IChartCriteria;
    items: Array<PacketProgress> = [];

    isLoaded: boolean;

    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $loggingService: LoggingService
    )
    {
        $chartManagerService.refreshChartObservable
            .pipe(takeUntil(this.destructor$))
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId)
                {
                    return;
                }
                this.loadChartData();
            });
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria) { this._chartCriteria = value; }

    get showChart(): boolean
    {
        return !!this.chartCriteria?.ErrorMessage || this.hasData;
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            (_resolveFn, rejectFn) =>
            {
                if (!this.hasData)
                {
                    return this.loadChartData();
                }
                try
                {
                    this.updateChartConfiguration();
                }
                catch (err)
                {
                    rejectFn(err);
                }
            })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    get hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData)
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private updateChartConfiguration(): void
    {
        this.chartCriteria.ChartDatas[0].Data[0]
            .forEach(chartData =>
            {
                const packetProgress: PacketProgress = {
                    ProcessId: chartData.ProcessId,
                    PacketId: chartData.PacketId,
                    ProcessType: chartData.ProcessType,
                    PacketNumber: chartData.PacketNumber,
                    PacketDescription: chartData.PacketDescription,
                    CurrentStep: chartData.CurrentStep,
                    CompletedSteps: chartData.CompletedSteps,
                    TotalSteps: chartData.TotalSteps,
                    UniqueData: chartData.UniqueData || [],
                };

                this.items.push(packetProgress);
            });
    }
}
